import { Avatar, Table, TableColumnsType, Tooltip } from "antd"
import { useMemo, useState } from "react"
import { IRole } from "./interface/interface"
import { Button } from "components"
import { AppSearchInput } from "components/inputs/AppSearchInput"
import ResponsiveActinBtn from "components/new-stuff/ui/ResponsiveActinBtn"
import { useNavigate } from "react-router-dom"
import { DeleteCustomRoleModal } from "./modals/DeleteCustomRoleModal"
import { AssignMembersModal } from "./modals/AssignMembersModal"
import { useAppNotificationStore, useAuthStore } from "stores"
import { ChangePrimaryAdminModal } from "./modals/ChangePrimaryAdminModal"
import { AppTable, TableActionSelect } from "components/new-stuff/app-table"
import { ROLE_ENDPOINTS, roleService } from "services/roleService"
import { RoleDetailsDataEntityRes } from "stores/setting/permissions"
import { useBackendTableQuery } from "pages/dashboard/performance/components/useBackendTableQuery"

type Props = {
  type: "custom" | "system"
}

// Hidden from both the custom and system lists (RolesPicker excludes it too).
const EXCLUDED_ROLE_ID = 3

export const RolesDataList = ({ type }: Props) => {
  const { toast } = useAppNotificationStore()

  const { user } = useAuthStore()

  const navigate = useNavigate()

  const {
    search,
    tablePagination,
    handleSearchChange,
    data,
    isLoading,
    isSearching,
    refetch,
  } = useBackendTableQuery<RoleDetailsDataEntityRes>({
    queryKeyBase: [ROLE_ENDPOINTS.SELECT_ROLE_DETAILS, type === "system"],
    queryFn: async (params) =>
      roleService.selectRoleDetails({
        is_default: type === "system",
        ...params,
      }),
    getTotal: (response) => response?.data?.totalDocs || 0,
    searchParamName: "searchParams",
  })

  const tableData = useMemo(
    () =>
      data?.data?.docs?.filter((role) => role?.role_id !== EXCLUDED_ROLE_ID) ||
      [],
    [data]
  )

  const [itemToDelete, setItemToDelete] = useState<IRole>()
  const [itemToAssignMember, setItemToAssignMembers] = useState<IRole>()
  const [assignPrimaryAdmin, setAssignPrimaryAdmin] = useState<IRole>()

  const columns: TableColumnsType<IRole> = [
    {
      title: "Role",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Description",
      dataIndex: "alias",
      align: "left",
      width: "40%",
      render(value, record, index) {
        return <>{record?.alias || ""}</>
      },
    },
    {
      title: "Members",
      width: "30%",
      render(value, record, index) {
        const canTakeActionOnPrimaryAdmin = record?.role_id === 1
        const members = record?.roleMembers?.filter(Boolean) ?? []
        return (
          <div className="flex gap-2 items-center">
            {members.length > 0 ? (
              <Tooltip
                arrow
                autoAdjustOverflow
                placement="left"
                title={
                  <div className="flex flex-col gap-1">
                    {record.roleMembers?.map((role) => (
                      <div className="flex gap-2 items-center">
                        <div>
                          <Avatar
                            src={role?.avatar_url}
                            size={"small"}
                          />
                        </div>
                        <div>{role?.nick_name}</div>
                      </div>
                    ))}
                  </div>
                }
                trigger={"hover"}
                className="cursor-pointer"
              >
                <div>
                  {record?.roleMembers?.length}&nbsp;
                  {record?.roleMembers?.length === 1 ? "member" : "members"}
                </div>
              </Tooltip>
            ) : (
              "No member"
            )}

            <div
              className={`cursor-pointer whitespace-nowrap text-primary500  py-1 px-2 text-[12px] select-none rounded-[5px]`}
              onClick={() => {
                if (canTakeActionOnPrimaryAdmin) {
                  if (user?.isPrimaryAdmin === true) {
                    setAssignPrimaryAdmin(record)
                  } else {
                    toast.error(
                      "You don't have sufficient permission to change the primary admin"
                    )
                  }
                } else {
                  setItemToAssignMembers(record)
                }
              }}
            >
              {canTakeActionOnPrimaryAdmin ? "Change Admin" : "Assign members"}
            </div>
          </div>
        )
      },
    },
    {
      title: "",
      render(value, record, index) {
        const viewLink = `view/${record?.role_key}`
        return (
          <TableActionSelect
            items={
              type === "custom"
                ? [
                    {
                      key: "1",
                      label: (
                        <button
                          className="w-full text-left"
                          onClick={() => navigate(viewLink)}
                        >
                          View
                        </button>
                      ),
                    },

                    {
                      key: "2",
                      label: (
                        <button
                          className="w-full text-left"
                          onClick={() => {
                            setItemToDelete(record)
                          }}
                        >
                          Delete
                        </button>
                      ),
                    },
                  ]
                : [
                    {
                      key: "1",
                      label: (
                        <button
                          className="w-full text-left"
                          onClick={() => navigate(viewLink)}
                        >
                          View
                        </button>
                      ),
                    },
                  ]
            }
          />
        )
      },
    },
  ]

  return (
    <div className="flex flex-col justify-between h-full w-full">
      <div className="bg-white  flex items-center justify-between gap-4 py-4 px-4">
        <div className="w-full  flex items-center gap-4 justify-between">
          <AppSearchInput
            search={search}
            handleSearch={(value) => handleSearchChange(value)}
          />

          {type === "custom" && (
            <ResponsiveActinBtn
              items={[
                {
                  key: "1",
                  label: (
                    <button
                      onClick={() => {
                        navigate("create")
                      }}
                    >
                      Create custom role
                    </button>
                  ),
                },
              ]}
            >
              <Button
                color="primary"
                className="h-full"
                onClick={() => {
                  navigate("create")
                }}
              >
                Create Custom Role
              </Button>
            </ResponsiveActinBtn>
          )}
        </div>
      </div>

      <AppTable
        columns={columns}
        loading={isLoading || isSearching}
        dataSource={tableData}
        scroll={{
          y: "calc(100vh - 300px)",
          x: "max-content",
        }}
        tableLayout="auto"
        pagination={tablePagination}
        rowClassName="text-neutral400 font-avenir"
      />

      {itemToDelete && (
        <DeleteCustomRoleModal
          role={itemToDelete}
          onClose={(status: boolean) => {
            setItemToDelete(undefined)
            if (status) {
              refetch()
            }
          }}
        />
      )}
      {itemToAssignMember && (
        <AssignMembersModal
          role={itemToAssignMember}
          onClose={(status: boolean) => {
            setItemToAssignMembers(undefined)
            if (status) {
              refetch()
            }
          }}
          isCustomRole={type === "system" ? false : true}
        />
      )}
      {assignPrimaryAdmin && (
        <ChangePrimaryAdminModal
          role={assignPrimaryAdmin}
          onClose={(status: boolean) => {
            setAssignPrimaryAdmin(undefined)
            if (status) {
              refetch()
            }
          }}
        />
      )}
    </div>
  )
}
